/**
 * Notifications — mobile-parity notification list with tabs (All / Unread /
 * Buddies), sectioned by Today / Earlier, and bulk actions in an overflow menu.
 * Tapping a post-related notification opens a post detail modal (CommentsModal
 * with fullView), mirroring the mobile PostDetails screen.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useFeed } from '@wlb/shared/context/FeedContext';
import feedService from '@wlb/shared/api/services/feedService';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { FeedPost } from '@wlb/shared/types/post';
import type { NotificationItem as NotificationItemType } from '@wlb/shared/types/notification';
import { ListStatus } from '../components/ui/ListStatus';
import { Tabs } from '../components/ui/Tabs';
import { PopoverMenu, type PopoverMenuItem } from '../components/ui/PopoverMenu';
import {
  BellIcon,
  CheckIcon,
  ChevronLeftIcon,
  MoreIcon,
  TrashBinIcon,
} from '../components/ui/icons';
import { useNotifications } from '../features/notifications/useNotifications';
import { useNotificationBadge } from '../features/notifications/NotificationBadgeContext';
import { NotificationItem } from '../features/notifications/NotificationItem';
import { NotificationsSkeleton } from '../features/notifications/NotificationsSkeleton';
import {
  setVisibleNotificationsSelected,
  toggleNotificationSelection,
} from '../features/notifications/notificationSelection';
import { CommentsModal } from '../features/feed/CommentsModal';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import '../components/ui/skeleton.css';
import '../features/profile/profile.css';
import '../features/notifications/notifications.css';

type NotificationTab = 'all' | 'unread' | 'buddies';
type MenuKey = 'markAllAsRead' | 'selectNotifications' | 'deleteAll';
type RowMenuKey = 'markAsRead' | 'delete';

interface RowMenuState {
  notification: NotificationItemType;
  anchorRect: DOMRect;
}

export function Notifications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getCachedPost } = useFeed();
  const openUserProfile = useOpenUserProfile();
  const {
    notifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteNotifications,
    deleteAllNotifications,
    acceptBuddyRequest,
    rejectBuddyRequest,
    joinGroupRequest,
  } = useNotifications();
  const notificationBadge = useNotificationBadge();
  const refreshNotificationBadge = notificationBadge?.refresh;

  // Loading may reconcile and delete stale group-request notifications. Read
  // the count again after that cleanup so the navigation badge follows the
  // list instead of keeping the pre-cleanup value.
  useEffect(() => {
    if (!loading && !error) {
      refreshNotificationBadge?.().catch(() => undefined);
    }
  }, [error, loading, refreshNotificationBadge]);

  /** Reading a row here changes the nav badge, which nothing else will notice. */
  const markRowRead = async (id: string): Promise<boolean> => {
    const success = await markAsRead(id);
    if (success) notificationBadge?.refresh().catch(() => undefined);
    return success;
  };

  const [activeTab, setActiveTab] = useState<NotificationTab>('all');
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [rowMenu, setRowMenu] = useState<RowMenuState | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [detailPost, setDetailPost] = useState<FeedPost | null>(null);
  const [detailCommentId, setDetailCommentId] = useState<string | null>(null);
  const [openingPostId, setOpeningPostId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NotificationItemType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteAllConfirmVisible, setDeleteAllConfirmVisible] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [deleteSelectedConfirmVisible, setDeleteSelectedConfirmVisible] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const tabItems = useMemo(
    () => [
      { key: 'all' as const, label: t('home.notifications.tabs.all') },
      { key: 'unread' as const, label: t('home.notifications.tabs.unread') },
      { key: 'buddies' as const, label: t('home.notifications.tabs.buddies') },
    ],
    [t],
  );

  const menuItems: PopoverMenuItem<MenuKey>[] = [
    { key: 'markAllAsRead', label: t('home.notifications.markAllAsRead'), icon: BellIcon },
    {
      key: 'selectNotifications',
      label: t('home.notifications.buttons.selectNotifications'),
      icon: CheckIcon,
    },
    {
      key: 'deleteAll',
      label: t('home.notifications.buttons.deleteAll'),
      icon: TrashBinIcon,
      destructive: true,
    },
  ];

  const rowMenuItems: PopoverMenuItem<RowMenuKey>[] = rowMenu
    ? [
        ...(!rowMenu.notification.isRead
          ? [{
              key: 'markAsRead' as const,
              label: t('home.notifications.buttons.markAsRead'),
              icon: CheckIcon,
            }]
          : []),
        {
          key: 'delete',
          label: t('home.notifications.buttons.deleteThisNotification'),
          icon: TrashBinIcon,
          destructive: true,
        },
      ]
    : [];

  const filtered = useMemo(() => {
    switch (activeTab) {
      case 'unread':
        return notifications.filter(n => !n.isRead);
      case 'buddies':
        return notifications.filter(n => n.type === 'buddyRequest');
      default:
        return notifications;
    }
  }, [notifications, activeTab]);

  const sections = useMemo(() => {
    const today = filtered.filter(n => n.section?.toLowerCase() === 'today');
    const earlier = filtered.filter(n => n.section?.toLowerCase() !== 'today');
    const list: { title: string; data: typeof filtered }[] = [];
    if (today.length > 0) list.push({ title: t('home.notifications.sections.today'), data: today });
    if (earlier.length > 0) list.push({ title: t('home.notifications.sections.earlier'), data: earlier });
    return list;
  }, [filtered, t]);

  const selectedCount = selectedIds.size;
  const allVisibleSelected = filtered.length > 0
    && filtered.every(notification => selectedIds.has(notification.id));
  const someVisibleSelected = filtered.some(notification => selectedIds.has(notification.id));

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [allVisibleSelected, someVisibleSelected]);

  // Convertible laptops flip the (hover)/(pointer) media features by mode, so
  // capability queries mislabel a mouse user as touch. Trust usage instead:
  // pin the row kebabs visible only after a real touch happens on the page.
  const [touchUsed, setTouchUsed] = useState(false);
  useEffect(() => {
    if (touchUsed) return undefined;
    const markTouch = (): void => setTouchUsed(true);
    window.addEventListener('touchstart', markTouch, { once: true, passive: true });
    return () => window.removeEventListener('touchstart', markTouch);
  }, [touchUsed]);

  const exitSelectionMode = (): void => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setDeleteSelectedConfirmVisible(false);
  };

  const handleTabChange = (tab: NotificationTab): void => {
    exitSelectionMode();
    setActiveTab(tab);
  };

  const toggleSelected = (notification: NotificationItemType): void => {
    setSelectedIds(current => toggleNotificationSelection(current, notification.id));
  };

  const toggleSelectAllVisible = (): void => {
    setSelectedIds(current => setVisibleNotificationsSelected(
      current,
      filtered.map(notification => notification.id),
      !allVisibleSelected,
    ));
  };

  const handlePress = async (item: NotificationItemType) => {
    if (openingPostId) return;
    if (!item.isRead) {
      const success = await markRowRead(item.id);
      if (!success) return;
    }

    // Buddy invite notifications are matched by name, not by resolved type:
    // "Buddy Invite Accepted" is classified as 'general' because it no longer
    // carries action buttons, so the type check below misses it. Mobile handles
    // these first and so does the web.
    const typeName = (item.notificationTypeName || '').trim().toLowerCase();

    if (typeName === 'buddy invite received') {
      navigate('/connections?tab=buddies&subtab=requested');
      return;
    }

    if (typeName === 'buddy invite accepted') {
      navigate('/connections?tab=buddies&subtab=accepted');
      return;
    }

    // Chat notification → open the conversation.
    if (item.conversationId) {
      navigate(`/messages/${item.conversationId}`);
      return;
    }

    // Group notifications (group posts carry both postId and groupId) open the
    // group itself — the post lives in the group's feed, so the group is the
    // canonical destination, exactly like mobile's GROUP_DETAILS navigation.
    if (item.groupId) {
      navigate(`/groups/${item.groupId}`);
      return;
    }

    // Post-related notification (like, comment) → open post detail modal. The
    // feed cache only holds the currently loaded page, so fall back to GetPost
    // the way mobile's PostDetails screen does.
    if (item.postId) {
      const postId = String(item.postId);
      const commentId = item.postCommentId != null ? String(item.postCommentId) : null;
      const cached = getCachedPost?.(postId);
      if (cached) {
        setDetailCommentId(commentId);
        setDetailPost(cached);
        return;
      }

      setOpeningPostId(postId);
      try {
        const response = await feedService.getPostDetails(postId);
        if (response.success && response.data) {
          setDetailCommentId(commentId);
          setDetailPost(response.data.post);
        } else {
          ToastService.show({ type: 'error', message: t('home.notifications.postNotFound') });
        }
      } catch {
        ToastService.show({ type: 'error', message: t('home.notifications.postNotFound') });
      } finally {
        setOpeningPostId(null);
      }
      return;
    }

    // Buddy request (resolved type) → open the Buddies / Requested tab so the
    // user can respond. The name-based checks above already caught the named
    // variants; this catches any buddyRequest-typed row that slipped through.
    if (item.type === 'buddyRequest') {
      navigate('/connections?tab=buddies&subtab=requested');
      return;
    }

    // Rows about a person and no content (follow, profile activity) open the
    // actor. Rows carrying no actor either — system notices — stay inert.
    if (item.actorUserGuid) {
      openUserProfile(item.actorUserGuid);
    }
  };

  const handleView = (item: NotificationItemType) => {
    const viewGroup = async () => {
      if (!item.isRead) {
        const success = await markRowRead(item.id);
        if (!success) return;
      }
      if (item.groupId) navigate(`/groups/${item.groupId}`);
    };
    viewGroup().catch(() => undefined);
  };

  const handleJoinGroupRequest = async (item: NotificationItemType) => {
    const success = await joinGroupRequest(item);
    if (success) notificationBadge?.refresh().catch(() => undefined);
  };

  const handleOpenProfile = async (item: NotificationItemType) => {
    if (!item.isRead) {
      const success = await markRowRead(item.id);
      if (!success) return;
    }

    let actorGuid = item.actorUserGuid;
    if (!actorGuid && item.postId) {
      const postId = String(item.postId);
      const cached = getCachedPost?.(postId);
      actorGuid = cached?.profileUserGuid ?? undefined;

      if (!actorGuid) {
        setOpeningPostId(postId);
        try {
          const response = await feedService.getPostDetails(postId);
          actorGuid = response.success
            ? response.data?.post.profileUserGuid ?? undefined
            : undefined;
        } catch {
          actorGuid = undefined;
        } finally {
          setOpeningPostId(null);
        }
      }
    }

    openUserProfile(actorGuid);
  };

  const handleAvatarPress = (item: NotificationItemType) => {
    handleOpenProfile(item).catch(() => undefined);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const success = await deleteNotification(deleteTarget.id);
      if (success) {
        setDeleteTarget(null);
        notificationBadge?.refresh().catch(() => undefined);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmDeleteAll = async () => {
    if (deletingAll) return;
    setDeletingAll(true);
    try {
      const success = await deleteAllNotifications();
      if (success) {
        setDeleteAllConfirmVisible(false);
        notificationBadge?.refresh().catch(() => undefined);
      }
    } finally {
      setDeletingAll(false);
    }
  };

  const handleConfirmDeleteSelected = async (): Promise<void> => {
    if (deletingSelected || selectedCount === 0) return;
    setDeletingSelected(true);
    try {
      const result = await deleteNotifications(Array.from(selectedIds));
      setDeleteSelectedConfirmVisible(false);
      setSelectedIds(new Set(result.failedIds));

      if (result.deletedIds.length > 0) {
        ToastService.show({
          type: 'success',
          message: t('home.notifications.toasts.selectedDeleted', {
            count: result.deletedIds.length,
          }),
        });
        notificationBadge?.refresh().catch(() => undefined);
      }

      if (result.failedIds.length === 0) {
        setSelectionMode(false);
      }
    } finally {
      setDeletingSelected(false);
    }
  };

  const handleMenuSelect = async (key: MenuKey) => {
    setMenuAnchorRect(null);
    if (key === 'selectNotifications') {
      if (notifications.length > 0) {
        setRowMenu(null);
        setSelectionMode(true);
        setSelectedIds(new Set());
      }
      return;
    }

    if (key === 'deleteAll') {
      if (notifications.length > 0) setDeleteAllConfirmVisible(true);
      return;
    }

    if (key === 'markAllAsRead') {
      if (markingAll) return;
      setMarkingAll(true);
      try {
        const success = await markAllAsRead();
        if (success) {
          notificationBadge?.refresh().catch(() => undefined);
          ToastService.show({ type: 'success', message: t('home.notifications.markAllAsRead') });
        }
      } finally {
        setMarkingAll(false);
      }
    }
  };

  const handleRowMenuSelect = async (key: RowMenuKey) => {
    const target = rowMenu?.notification;
    setRowMenu(null);
    if (!target) return;

    if (key === 'markAsRead') {
      await markRowRead(target.id);
      return;
    }

    setDeleteTarget(target);
  };

  return (
    <section
      className={`notifications-page${touchUsed ? ' notifications-page--touch' : ''}`}
      aria-label={t('home.notifications.title')}
    >
      <div className="profile-topbar">
        <button
          type="button"
          className="profile-topbar__back"
          aria-label={t('home.notifications.accessibility.back')}
          onClick={() => navigate(-1)}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <h1 className="profile-topbar__title">{t('home.notifications.title')}</h1>
        <button
          type="button"
          className="profile-topbar__button"
          aria-label={t('home.notifications.accessibility.openMenu')}
          aria-haspopup="dialog"
          aria-expanded={Boolean(menuAnchorRect)}
          onClick={event => setMenuAnchorRect(event.currentTarget.getBoundingClientRect())}
        >
          <MoreIcon size={20} />
        </button>
      </div>

      <Tabs
        items={tabItems}
        activeKey={activeTab}
        onChange={handleTabChange}
        label={t('home.notifications.accessibility.notificationsList')}
        idPrefix="notif-tab"
      />

      {selectionMode && (
        <div className="notification-selection-bar" role="region" aria-label={t('home.notifications.accessibility.selectionActions')}>
          <label className="notification-selection-bar__select-all">
            <input
              ref={selectAllCheckboxRef}
              type="checkbox"
              className="notification-selection-bar__checkbox"
              checked={allVisibleSelected}
              disabled={filtered.length === 0}
              aria-label={t(allVisibleSelected
                ? 'home.notifications.buttons.clearAll'
                : 'home.notifications.buttons.selectAll')}
              onChange={toggleSelectAllVisible}
            />
            <span className="notification-selection-bar__count" aria-live="polite">
              {t('home.notifications.selectionCount', { count: selectedCount })}
            </span>
          </label>
          <div className="notification-selection-bar__actions">
            <button
              type="button"
              className="notification-selection-bar__button notification-selection-bar__button--delete"
              disabled={selectedCount === 0}
              onClick={() => setDeleteSelectedConfirmVisible(true)}
            >
              <TrashBinIcon size={18} />
              {t('home.notifications.buttons.deleteSelected', { count: selectedCount })}
            </button>
            <button
              type="button"
              className="notification-selection-bar__button"
              onClick={exitSelectionMode}
            >
              {t('common.buttons.cancel')}
            </button>
          </div>
        </div>
      )}

      <div
        role="tabpanel"
        id={`notif-tab-panel-${activeTab}`}
        aria-labelledby={`notif-tab-${activeTab}`}
      >
        {openingPostId && (
          <p className="notification-loading" role="status">{t('common.loading')}</p>
        )}

        {loading && notifications.length === 0 && <NotificationsSkeleton />}

        {!loading && error && notifications.length === 0 && (
          <ListStatus
            variant="error"
            icon={<BellIcon size={48} />}
            title={t('home.notifications.loadFailed')}
            message={error}
            onAction={() => { fetchNotifications().catch(() => undefined); }}
            busy={loading}
          />
        )}

        {!loading && !error && filtered.length === 0 && (
          <ListStatus
            variant="empty"
            icon={<BellIcon size={48} />}
            title={t('home.notifications.emptyTitle')}
            message={t('home.notifications.emptyDescription')}
            onAction={() => { fetchNotifications().catch(() => undefined); }}
            busy={loading}
          />
        )}

        {sections.map(section => (
          <div className="notification-section skeleton-fade-in" key={section.title}>
            <h2 className="notification-section__header">{section.title}</h2>
            {section.data.map(item => (
              <NotificationItem
                key={item.id}
                notification={item}
                onPress={handlePress}
                onAccept={acceptBuddyRequest}
                onReject={rejectBuddyRequest}
                onJoin={notification => {
                  handleJoinGroupRequest(notification).catch(() => undefined);
                }}
                onView={handleView}
                onOpenProfile={handleAvatarPress}
                onMenu={(notification, anchorRect) => setRowMenu({ notification, anchorRect })}
                selectionMode={selectionMode}
                selected={selectedIds.has(item.id)}
                onToggleSelected={toggleSelected}
                menuOpen={rowMenu?.notification.id === item.id}
              />
            ))}
          </div>
        ))}
      </div>

      <PopoverMenu
        anchorRect={menuAnchorRect}
        items={menuItems}
        label={t('home.notifications.accessibility.openMenu')}
        labelledBy="notif-menu-title"
        onClose={() => setMenuAnchorRect(null)}
        onSelect={key => { handleMenuSelect(key).catch(() => undefined); }}
      />

      <PopoverMenu
        anchorRect={rowMenu?.anchorRect ?? null}
        items={rowMenuItems}
        label={t('home.notifications.accessibility.notificationMenu')}
        labelledBy="notification-row-menu-title"
        onClose={() => setRowMenu(null)}
        onSelect={key => { handleRowMenuSelect(key).catch(() => undefined); }}
      />

      {detailPost && (
        <CommentsModal
          post={detailPost}
          fullView
          highlightCommentId={detailCommentId}
          onClose={() => { setDetailPost(null); setDetailCommentId(null); }}
          onCommentAdded={() => undefined}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('home.notifications.deleteConfirmTitle')}
          message={t('home.notifications.deleteConfirmMessage')}
          confirmLabel={t('common.buttons.delete')}
          cancelLabel={t('common.buttons.cancel')}
          loading={deleting}
          showIcon
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => { handleConfirmDelete().catch(() => undefined); }}
        />
      )}

      {deleteAllConfirmVisible && (
        <ConfirmModal
          title={t('home.notifications.deleteAllConfirmTitle')}
          message={t('home.notifications.deleteAllConfirmMessage')}
          confirmLabel={t('common.buttons.delete')}
          cancelLabel={t('common.buttons.cancel')}
          loading={deletingAll}
          showIcon
          onClose={() => setDeleteAllConfirmVisible(false)}
          onConfirm={() => { handleConfirmDeleteAll().catch(() => undefined); }}
        />
      )}

      {deleteSelectedConfirmVisible && (
        <ConfirmModal
          title={t('home.notifications.deleteSelectedConfirmTitle')}
          message={t('home.notifications.deleteSelectedConfirmMessage', {
            count: selectedCount,
          })}
          confirmLabel={t('home.notifications.buttons.deleteSelected', {
            count: selectedCount,
          })}
          cancelLabel={t('common.buttons.cancel')}
          loading={deletingSelected}
          showIcon
          onClose={() => setDeleteSelectedConfirmVisible(false)}
          onConfirm={() => { handleConfirmDeleteSelected().catch(() => undefined); }}
        />
      )}
    </section>
  );
}
